import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketCheckbox, MarketDivider, MarketRow } from '@market/react';
import { WalletPaymentSource } from 'typescript/types/api/payment_sources';
import styles from 'typescript/apps/checkout_v3/components/payment/saved_cards/saved_cards_options.module.scss';
import { onCheckoutPageClick } from 'typescript/utilities/tracking';
import { CHECKOUT_PAGE_CLICK_EVENTS } from 'typescript/constants/tracking';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';

interface SavedCardsOptionsProps extends HTMLAttributes<HTMLElement> {
  userIsLoggedIn: boolean;
  orderIsWarranty: boolean;
  savedCardsEnabled: boolean;
  selectedCard?: WalletPaymentSource;
  saveCardToWallet: boolean;
  setSaveCardToWallet: (value: boolean) => void;
  makeDefaultCard: boolean;
  setMakeDefaultCard: (value: boolean) => void;
}

const SavedCardsOptions = ({
  userIsLoggedIn,
  orderIsWarranty,
  savedCardsEnabled,
  selectedCard,
  saveCardToWallet,
  setSaveCardToWallet,
  makeDefaultCard,
  setMakeDefaultCard,
  className,
}: SavedCardsOptionsProps) => {
  const { t } = useTranslation();
  const { trackingData } = useCheckoutFlow();

  const canSaveCard = () => {
    if (selectedCard) {
      return false;
    }

    return savedCardsEnabled && userIsLoggedIn && !orderIsWarranty;
  };

  const canSetDefaultCard = () => {
    if (selectedCard) {
      return selectedCard.default === false;
    }

    return savedCardsEnabled && userIsLoggedIn && !orderIsWarranty;
  };

  return (
    <div className={className}>
      {canSaveCard() && (
        <div>
          <div className="shop-grid-item-full">
            <MarketRow
              data-testid="save-card"
              control-position="leading"
              className={styles.saveCardContainer}
              size="medium"
              selected={saveCardToWallet}
              onMarketRowSelected={() => {
                onCheckoutPageClick({
                  eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.save_card_enabled,
                  trackingData,
                });
                setSaveCardToWallet(true);
              }}
              onMarketRowDeselected={() => {
                onCheckoutPageClick({
                  eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.save_card_disabled,
                  trackingData,
                });
                setSaveCardToWallet(false);
              }}
            >
              <label slot="label">
                {String(t('checkout.payment.saveCard'))}
              </label>
              <p slot="subtext" className="paragraph-20">
                {String(t('checkout.payment.suggestCard'))}
              </p>

              <MarketCheckbox slot="control"></MarketCheckbox>
            </MarketRow>
          </div>
          <div className="shop-grid-item-full">
            <MarketDivider className={styles.dividerMargin} size="thin" />
          </div>
        </div>
      )}
      {canSetDefaultCard() && (
        <div>
          <div className="shop-grid-item-full">
            <MarketRow
              data-testid="set-card-as-default"
              control-position="leading"
              className={styles.defaultCardContainer}
              size="medium"
              selected={makeDefaultCard}
              onMarketRowSelected={() => {
                setMakeDefaultCard(true);
                onCheckoutPageClick({
                  eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.default_card_enabled,
                  trackingData,
                });
              }}
              onMarketRowDeselected={() => {
                setMakeDefaultCard(false);
                onCheckoutPageClick({
                  eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.default_card_disabled,
                  trackingData,
                });
              }}
            >
              <label slot="label">
                {String(t('checkout.payment.setDefaultCard'))}
              </label>
              <MarketCheckbox slot="control"></MarketCheckbox>
            </MarketRow>
          </div>
          <div className="shop-grid-item-full">
            <MarketDivider className={styles.dividerMargin} size="thin" />
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedCardsOptions;
